const LISTING_ANCHOR = "resources-listing";
const AJAX_ACTION = "natech_resource_listing";

const scrollToListing = (section) => {
	if (!section || window.location.hash !== `#${LISTING_ANCHOR}`) {
		return;
	}

	window.requestAnimationFrame(() => {
		section.scrollIntoView({ block: "start" });
	});
};

const getPageFromUrl = (href) => {
	const url = new URL(href, window.location.origin);
	const pathMatch = url.pathname.match(/\/page\/(\d+)\/?$/);

	if (pathMatch) {
		return parseInt(pathMatch[1], 10);
	}

	const page = parseInt(url.searchParams.get("page") || "1", 10);

	return Number.isNaN(page) || page < 1 ? 1 : page;
};

const getFilterParam = (input) => input.dataset.filterParam || input.name.replace(/\[\]$/, "");

const getFilterValuesFromParams = (params, param) => {
	const values = new Set();
	const direct = params.get(param);

	if (direct) {
		direct.split(",").forEach((part) => {
			const value = part.trim();

			if (value) {
				values.add(value);
			}
		});
	}

	params.getAll(`${param}[]`).forEach((value) => {
		if (value) {
			values.add(value);
		}
	});

	return values;
};

const getCheckedFilterGroups = (form) => {
	const grouped = {};

	form.querySelectorAll(".resources-listing__filter-input:checked").forEach((input) => {
		const param = getFilterParam(input);

		if (!grouped[param]) {
			grouped[param] = [];
		}

		grouped[param].push(input.value);
	});

	return grouped;
};

const buildListingUrl = (form, page = 1) => {
	const url = new URL(form.action || window.location.href, window.location.origin);
	const params = new URLSearchParams();

	Object.entries(getCheckedFilterGroups(form)).forEach(([param, values]) => {
		if (values.length) {
			params.set(param, values.join(","));
		}
	});

	if (page > 1) {
		params.set("page", String(page));
	}

	url.search = params.toString();
	url.hash = LISTING_ANCHOR;

	return url;
};

const syncFiltersFromUrl = (form) => {
	const params = new URLSearchParams(window.location.search);

	form.querySelectorAll(".resources-listing__filter-input").forEach((input) => {
		const values = getFilterValuesFromParams(params, getFilterParam(input));
		input.checked = values.has(input.value);
	});
};

const setClearVisibility = (section, visible) => {
	const clearWrap = section.querySelector("[data-clear-wrap]");

	if (!clearWrap) {
		return;
	}

	clearWrap.classList.toggle("is-hidden", !visible);
};

const activateResultsAnimations = (main) => {
	main.querySelectorAll(".inview-animate--move-up").forEach((element) => {
		element.classList.add("inview-active");
	});
};

const initResourcesListing = (section) => {
	const form = section.querySelector(".resources-listing__filters");
	const main = section.querySelector("[data-listing-main]");
	const results = section.querySelector("[data-listing-results]");
	const ajaxUrl = section.dataset.ajaxUrl;
	const nonce = section.dataset.nonce;
	const postsPerPage = section.dataset.postsPerPage || "9";
	const clearUrl = section.dataset.clearUrl || form?.action || window.location.href;

	if (!form || !main || !results || !ajaxUrl || !nonce) {
		return;
	}

	let requestController = null;

	const setLoading = (loading) => {
		main.classList.toggle("is-loading", loading);

		if (loading) {
			main.style.minHeight = `${main.offsetHeight}px`;
			return;
		}

		main.style.minHeight = "";
	};

	const fetchListing = async (page = 1, pushState = true) => {
		if (requestController) {
			requestController.abort();
		}

		const controller = new AbortController();
		requestController = controller;
		setLoading(true);

		const body = new FormData();
		body.append("action", AJAX_ACTION);
		body.append("nonce", nonce);
		body.append("page", String(page));
		body.append("posts_per_page", postsPerPage);
		body.append("clear_url", clearUrl);

		Object.entries(getCheckedFilterGroups(form)).forEach(([param, values]) => {
			body.append(param, values.join(","));
		});

		try {
			const response = await fetch(ajaxUrl, {
				method: "POST",
				body,
				signal: controller.signal,
				credentials: "same-origin",
			});

			if (!response.ok) {
				throw new Error("Request failed");
			}

			const payload = await response.json();

			if (!payload?.success || !payload.data?.html) {
				throw new Error("Invalid response");
			}

			results.innerHTML = payload.data.html;
			setClearVisibility(section, Boolean(payload.data.has_filters));
			activateResultsAnimations(main);

			if (pushState) {
				const nextUrl = buildListingUrl(form, page);
				window.history.pushState({ resourceListing: true }, "", nextUrl.toString());
			}
		} catch (error) {
			if (error.name === "AbortError") {
				return;
			}

			window.location.assign(buildListingUrl(form, page).toString());
		} finally {
			if (requestController === controller) {
				setLoading(false);
				requestController = null;
			}
		}
	};

	section.querySelectorAll(".resources-listing__filter-toggle").forEach((button) => {
		button.addEventListener("click", () => {
			const group = button.closest(".resources-listing__filter-group");
			const panel = group?.querySelector(".resources-listing__filter-panel");
			const open = button.getAttribute("aria-expanded") === "true";

			if (!group || !panel) {
				return;
			}

			group.classList.toggle("is-open", !open);
			button.setAttribute("aria-expanded", open ? "false" : "true");
			panel.setAttribute("aria-hidden", open ? "true" : "false");
		});
	});

	form.querySelectorAll(".resources-listing__filter-input").forEach((input) => {
		input.addEventListener("change", () => {
			fetchListing(1);
		});
	});

	section.addEventListener("click", (event) => {
		const link = event.target.closest(".resources-listing__pagination a.page-numbers");

		if (!link || !section.contains(link)) {
			return;
		}

		event.preventDefault();
		fetchListing(getPageFromUrl(link.href));
	});

	section.addEventListener("click", (event) => {
		const clearLink = event.target.closest(".resources-listing__clear, .resources-listing__empty-link");

		if (!clearLink || !section.contains(clearLink)) {
			return;
		}

		event.preventDefault();

		form.querySelectorAll(".resources-listing__filter-input:checked").forEach((input) => {
			input.checked = false;
		});

		fetchListing(1);
	});

	window.addEventListener("popstate", () => {
		syncFiltersFromUrl(form);
		fetchListing(getPageFromUrl(window.location.href), false);
	});

	scrollToListing(section);
};

const boot = () => {
	document.querySelectorAll(".resources-listing").forEach((section) => {
		initResourcesListing(section);
	});
};

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", boot);
} else {
	boot();
}
